import { Box, Chip, Stack, Typography } from "@mui/material";
import React from "react";
import CustomStateChip from "../components/CustomStateChip";

export const DIFF_LEVELS = ["EASY", "MEDIUM", "HARD"];

/** Short preview of permutation array for the table cell. */
export function formatStartingLayout(layout) {
    if (!Array.isArray(layout)) return "—";
    const n = layout.length;
    const preview = layout.slice(0, Math.min(16, n));
    const tail = n > 16 ? ", …" : "";
    return `[${preview.join(", ")}${tail}] (${n})`;
}

/**
 * @param {Array<{ difficultyLevel?: string }>} templates
 * @param {string} level
 */
export function pickTemplate(templates, level) {
    if (!Array.isArray(templates)) return null;
    const byField = templates.find((t) => t?.difficultyLevel === level);
    if (byField) return byField;
    const idx = DIFF_LEVELS.indexOf(level);
    if (idx >= 0 && idx < templates.length) return templates[idx];
    return null;
}

/**
 * All templates for a difficulty (e.g. 6×6 and 12×12 both tagged EASY).
 * Sorted by gridSize ascending. Falls back like {@link pickTemplate} when no `difficultyLevel` on items.
 * @param {Array<{ difficultyLevel?: string, gridSize?: number }>} templates
 * @param {string} level
 */
export function templatesForDifficulty(templates, level) {
    if (!Array.isArray(templates)) return [];
    const byField = templates.filter((t) => t?.difficultyLevel === level);
    if (byField.length > 0) {
        return [...byField].sort((a, b) => (Number(a?.gridSize) || 0) - (Number(b?.gridSize) || 0));
    }
    const idx = DIFF_LEVELS.indexOf(level);
    if (idx >= 0 && idx < templates.length) {
        const t = templates[idx];
        return t ? [t] : [];
    }
    return [];
}

/** @typedef {"paid" | "free"} BillingFilter */

/**
 * Checkbox state for the story-level free flag.
 * With the new SlidingStoryGroupRo shape, free lives on `storySliding` (bundle row), not on
 * individual templates. Accepts either:
 *   - a group object `{ storySliding: { free }, templates: [] }` (new shape), or
 *   - a plain templates array (legacy / admin list rows that may not carry storySliding yet).
 *
 * @param {{ storySliding?: { free?: boolean }, templates?: Array } | Array<{ free?: boolean }>} groupOrTemplates
 * @returns {{ checked: boolean, indeterminate: boolean }}
 */
export function getStoryFreeCheckboxState(groupOrTemplates) {
    // New shape: caller passes the group object
    if (groupOrTemplates && !Array.isArray(groupOrTemplates) && typeof groupOrTemplates === "object") {
        const ss = groupOrTemplates.storySliding;
        if (ss && typeof ss === "object") {
            return { checked: ss.free === true, indeterminate: false };
        }
        // Fall through to legacy array path using group.templates
        return getStoryFreeCheckboxState(groupOrTemplates.templates ?? []);
    }
    // Legacy path: plain array of templates
    const templates = Array.isArray(groupOrTemplates) ? groupOrTemplates : [];
    if (templates.length === 0) return { checked: false, indeterminate: false };
    let freeTrue = 0;
    for (const t of templates) {
        if (t?.free === true) freeTrue += 1;
    }
    if (freeTrue === templates.length) return { checked: true, indeterminate: false };
    if (freeTrue === 0) return { checked: false, indeterminate: false };
    return { checked: false, indeterminate: true };
}

/**
 * Templates for a difficulty, optionally restricted to paid (`free !== true`) or free (`free === true`).
 * Note: with the new shape, `free` is a bundle-level flag on `storySliding`, not per-template.
 * These filters still work for legacy data where templates carry their own `free` field.
 * @param {Array<object>} templates
 * @param {string} level
 * @param {BillingFilter} billing
 */
export function filterTemplatesByBilling(templates, level, billing) {
    const list = templatesForDifficulty(templates, level);
    if (billing === "paid") return list.filter((t) => t?.free !== true);
    return list.filter((t) => t?.free === true);
}

/** Whether the group has any template matching billing for at least one difficulty. */
export function groupHasBillingTemplates(group, billing) {
    const templates = group?.templates;
    if (!Array.isArray(templates)) return false;
    return DIFF_LEVELS.some((level) => filterTemplatesByBilling(templates, level, billing).length > 0);
}

/** Distinct grid sizes present in templates, ascending (e.g. 6 then 12). */
export function uniqueGridSizesSorted(templates) {
    if (!Array.isArray(templates)) return [];
    const sizes = new Set();
    for (const t of templates) {
        const g = Number(t?.gridSize);
        if (Number.isFinite(g) && g > 0) sizes.add(g);
    }
    return [...sizes].sort((a, b) => a - b);
}

/**
 * Single template for a grid size + difficulty (for detail layouts ordered by size then level).
 * @param {Array<object>} templates
 * @param {number} gridSize
 * @param {string} level — EASY | MEDIUM | HARD
 */
export function templateForGridAndLevel(templates, gridSize, level) {
    const gs = Number(gridSize);
    if (!Number.isFinite(gs) || gs < 1 || !Array.isArray(templates)) return null;
    const forGs = templates.filter((t) => Number(t.gridSize) === gs);
    if (forGs.length === 0) return null;
    const byDiff = forGs.find((t) => t.difficultyLevel === level);
    if (byDiff) return byDiff;
    const idx = DIFF_LEVELS.indexOf(level);
    const hasDifficulty = forGs.some(
        (t) => t?.difficultyLevel != null && String(t.difficultyLevel).trim() !== ""
    );
    if (!hasDifficulty && idx >= 0 && idx < forGs.length) return forGs[idx];
    return null;
}

/**
 * Single SlidingRo summary: grid size, state, and created time.
 * free/paid is now a bundle-level flag on storySliding — not shown per template here.
 * Pass `storySliding` to show the bundle free badge alongside the template block.
 *
 * @param {{ tpl: object, storySliding?: { free?: boolean, publishedAt?: string } }} props
 */
export function SlidingTemplateBlock({ tpl, storySliding }) {
    if (!tpl) return null;
    // free comes from the bundle row when available, falls back to per-template for legacy data
    const isFree = storySliding != null ? storySliding.free === true : tpl.free === true;
    return (
        <Box sx={{ py: 0.25 }}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
                <Chip
                    size="small"
                    label={tpl.gridSize != null ? `${tpl.gridSize}×${tpl.gridSize}` : "—"}
                    variant="outlined"
                />
                <CustomStateChip size="small" label={tpl.state != null ? String(tpl.state) : "—"} />
                <Chip
                    size="small"
                    label={isFree ? "Free" : "Paid"}
                    color={isFree ? "secondary" : "default"}
                    variant={isFree ? "filled" : "outlined"}
                />
            </Stack>
            {tpl.createdAt ? (
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.25 }}>
                    {new Date(tpl.createdAt).toLocaleString()}
                </Typography>
            ) : null}
        </Box>
    );
}

/**
 * One difficulty column: all templates for that level.
 * @param {{ templates: Array<object>, level: string, storySliding?: object }} props
 */
export function DifficultyColumn({ templates, level, storySliding }) {
    const list = templatesForDifficulty(templates, level);
    if (list.length === 0) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    return (
        <Stack spacing={1} sx={{ py: 0.25 }}>
            {list.map((tpl) => (
                <SlidingTemplateBlock
                    key={tpl.id ?? `${level}-${tpl.gridSize}-${tpl.createdAt}`}
                    tpl={tpl}
                    storySliding={storySliding}
                />
            ))}
        </Stack>
    );
}

/**
 * One difficulty column for browse: all templates for that level + billing.
 * With the new shape, billing is determined by storySliding.free (bundle-level).
 * @param {{ templates: Array<object>, level: string, billing: BillingFilter, storySliding?: object }} props
 */
export function DifficultyBillingColumn({ templates, level, billing, storySliding }) {
    const list = filterTemplatesByBilling(templates, level, billing);
    if (list.length === 0) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    return (
        <Stack spacing={1} sx={{ py: 0.25 }}>
            {list.map((tpl) => (
                <SlidingTemplateBlock
                    key={tpl.id ?? `${level}-${tpl.gridSize}-${tpl.createdAt}`}
                    tpl={tpl}
                    storySliding={storySliding}
                />
            ))}
        </Stack>
    );
}

/** One cell: single SlidingRo summary (legacy single-template picker). */
export function TemplateCell({ tpl, storySliding }) {
    if (!tpl) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    return <SlidingTemplateBlock tpl={tpl} storySliding={storySliding} />;
}
